import { Controller } from "@hotwired/stimulus"
import { create, supported } from "@github/webauthn-json"

// Drives the WebAuthn registration ceremony. Reads the server-generated creation
// options from a data value, runs navigator.credentials.create() via the
// webauthn-json ponyfill, and POSTs the result back as JSON.
export default class extends Controller {
  static values = { options: Object, createUrl: String }
  static targets = ["nickname", "status", "button"]

  connect() {
    if (!supported()) {
      this.disable("This browser doesn't support passkeys. Use a different device or browser.")
    }
  }

  async register(event) {
    event.preventDefault()
    this.setStatus("Follow your device's prompt…")

    let credential
    try {
      credential = await create({ publicKey: this.optionsValue })
    } catch (error) {
      if (error.name === "NotAllowedError") {
        this.setStatus("Cancelled. Tap Create passkey to try again.", true)
      } else if (error.name === "InvalidStateError") {
        this.setStatus("This device already has a passkey for your account.", true)
      } else {
        this.setStatus("Couldn't create a passkey. Try again.", true)
      }
      return
    }

    const nickname = this.hasNicknameTarget ? this.nicknameTarget.value : ""
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        body: JSON.stringify({ publicKeyCredential: credential, nickname })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        window.Turbo.visit(data.redirect_to)
      } else {
        this.setStatus(data.error || "Couldn't save the passkey. Try again.", true)
      }
    } catch {
      this.setStatus("Network error. Try again.", true)
    }
  }

  disable(message) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = true
    this.setStatus(message, true)
  }

  setStatus(text, isError = false) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("text-red-600", isError)
    this.statusTarget.classList.toggle("text-gray-500", !isError)
  }
}
